import type { FC } from 'react';
import { DrawerSection } from '@components/DrawerSection';
import InputTextarea from '@components/InputTextarea';
import Toggle from '@components/Toggle';
import { type BlockEditFormState, isCreateMode } from '@ContractBuilder/modules/block-edit';
import type { BlockType } from '@domain/schemas/block.schema';
import { useVariationsTabs } from '@providers/variations-tabs';
import { isClauseBlock } from '@root/helpers/blocks';
import LayoutOptions from '@src/components/Sidebar/BlockLayoutSelector/LayoutSelector';

import { useBlockPanelContent } from '../hooks/useBlockPanelContent';
import type { BlockEditFormSetter, ToggleFormField } from '../types';
import type { PanelMode } from '../utils/get-panel-mode';

import { DrawerCollapsibleGroup } from './DrawerCollapsibleGroup';
import { VariationToggle } from './VariationToggle';

interface DrawerPanelSettingsProps {
  formValues?: BlockEditFormState;
  isBlockType: boolean;
  mode: PanelMode;
  setFormValues: BlockEditFormSetter;
  toggleField: ToggleFormField;
}

export const DrawerPanelSettings: FC<DrawerPanelSettingsProps> = ({
  formValues,
  isBlockType,
  mode,
  setFormValues,
  toggleField,
}) => {
  const { isAdminView } = mode;
  const { getContent } = useBlockPanelContent(isBlockType);
  const { variationsCount } = useVariationsTabs();

  const handleTypeChange = (value: BlockType) => {
    const isClauseValue = isClauseBlock({ type: value });
    const clauseResetPayload = isClauseValue
      ? {}
      : { clause_type: undefined, clause_reference: undefined, classification: undefined };

    setFormValues((prev) => ({ ...prev, ...clauseResetPayload, type: value }));
  };

  const hasVariations = variationsCount > 0;
  const isLayoutDisabled = !isCreateMode(formValues) && hasVariations;

  return (
    <DrawerCollapsibleGroup title={getContent('groups.settings')} id="settings">
      <div className="flex flex-col gap-3">
        {isAdminView && (
          <VariationToggle label={getContent('fields.settings.variations.label')} formValues={formValues} />
        )}
        <Toggle
          labelText={getContent('fields.settings.pageBreak.label')}
          value={Boolean(formValues?.shouldInsertPageBreak)}
          onClick={toggleField('shouldInsertPageBreak')}
          data-testid="toggle-page-break"
        />
      </div>
      <DrawerSection title={getContent('fields.layout.label')}>
        <LayoutOptions
          onChange={handleTypeChange}
          type={formValues?.type ?? 'mrc-heading'}
          isDisabled={isLayoutDisabled}
        />
      </DrawerSection>
      {isAdminView && (
        <DrawerSection title={getContent('fields.instructions.label')}>
          <InputTextarea
            placeholder={getContent('fields.instructions.placeholder')}
            name="helperText"
            onChange={(event) => setFormValues((prev) => ({ ...prev, helperText: event.target.value }))}
            value={formValues?.helperText}
            dataTestId="textarea:drawerPanelSettings:helperText"
          />
        </DrawerSection>
      )}
    </DrawerCollapsibleGroup>
  );
};
